import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Mail, 
  CheckCircle2, 
  XCircle, 
  Search, 
  BarChart3, 
  FileCheck, 
  Rocket,
  Shield,
  Target,
  Zap,
  Users,
  Clock,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import SEO from '../components/SEO';

const AnimatedCounter = ({ target, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-slate-700/50 rounded-2xl overflow-hidden bg-slate-800/30 backdrop-blur-sm">
    <button
      onClick={onClick}
      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-700/20 transition-colors"
    >
      <span className="font-semibold text-white pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-emerald-400 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
      )}
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5 px-6' : 'max-h-0'}`}>
      <p className="text-slate-300 leading-relaxed">{answer}</p>
    </div>
  </div>
);

const AutoMatePage = () => {
  const [visibleSections, setVisibleSections] = useState({});
  const [openFAQ, setOpenFAQ] = useState(0);

  const observeSection = (id) => (entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        setVisibleSections(prev => ({ ...prev, [id]: true }));
      }
    });
  };

  useEffect(() => {
    const observers = [];
    const sections = ['hero', 'stats', 'whofor', 'process', 'principles', 'faq', 'cta'];
    
    sections.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        const observer = new IntersectionObserver(observeSection(id), { threshold: 0.1 });
        observer.observe(element);
        observers.push(observer);
      }
    });

    return () => observers.forEach(obs => obs.disconnect());
  }, []);

  const faqs = [
    {
      question: "What types of processes can you audit?",
      answer: "We specialize in analyzing repetitive workflows across operations, marketing, sales, HR, and customer support. Any process that involves manual steps, data entry, or tool-switching is a candidate for automation assessment."
    },
    {
      question: "Do you sell automation tools or software?",
      answer: "No. We are 100% tool-agnostic with zero vendor partnerships or commissions. Our recommendations are based purely on what's best for your business, not what earns us referral fees."
    },
    {
      question: "How long does an automation audit take?",
      answer: "A typical audit takes 2-4 weeks depending on complexity. This includes discovery interviews, process mapping, tool analysis, and delivering your custom roadmap with ROI projections."
    },
    {
      question: "Is my data secure during the audit?",
      answer: "Absolutely. All engagements are protected under NDA and MOU agreements. We follow GDPR compliance and never share your business information with third parties."
    },
    {
      question: "What if you recommend NOT to automate?",
      answer: "That's part of our value. We'll tell you honestly when automation isn't the right solution. Sometimes process improvement or training is more effective than adding technology."
    }
  ];

  const processSteps = [
    {
      num: "01",
      title: "Discover & Assess",
      description: "We analyze your current processes, interview key stakeholders, and identify genuine automation opportunities. Every system is mapped for clarity.",
      icon: Search
    },
    {
      num: "02", 
      title: "Analyze & Score",
      description: "Using our proven framework, we score each process for automation potential, assess risks, and calculate realistic ROI projections.",
      icon: BarChart3
    },
    {
      num: "03",
      title: "Recommend & Plan",
      description: "Receive clear, actionable recommendations with transparent pricing, implementation roadmaps, and tool-agnostic guidance.",
      icon: FileCheck
    },
    {
      num: "04",
      title: "Implement & Support",
      description: "We support implementation with ongoing optimization, ensuring long-term success and measurable results.",
      icon: Rocket
    }
  ];

  const principles = [
    {
      icon: Shield,
      title: "Radical Transparency",
      description: "We publicly share our decision-making criteria and tell you when NOT to automate. No hidden agendas. UK registered, GDPR compliant."
    },
    {
      icon: Target,
      title: "Tool Agnostic",
      description: "Zero vendor partnerships. Zero commissions. Every recommendation is backed by thorough analysis of 200+ platforms."
    },
    {
      icon: Zap,
      title: "Research-Led",
      description: "We don't guess. Every piece of guidance is grounded in systematic research, real-world testing, and proven methodologies."
    },
    {
      icon: Users,
      title: "Built for UK SMEs",
      description: "Deep understanding of UK business practices, regulations, and the specific challenges of growing companies."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO 
        title="Auto-Mate | UK SME Automation Consulting | Cut Through the Noise"
        description="The automation skeptic's consultant. Honest, research-led automation guidance for UK SMEs. Tool-agnostic audits, ROI analysis, and implementation support. 200+ tools analyzed."
        canonical="https://www.owlworklabs.com/automate"
      />
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 pt-3 sm:pt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-2xl bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 shadow-xl">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="OwlWorkLabs Logo" 
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-lg sm:text-xl font-bold text-white">Auto-Mate</span>
              <span className="text-xs text-slate-400 hidden sm:block">by OwlWorkLabs</span>
            </div>
          </Link>
          
          <div className="flex items-center gap-3">
            <Link 
              to="/"
              className="hidden sm:flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
            <a 
              href="mailto:owlworklabs@gmail.com?subject=Auto-Mate Audit Request"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold rounded-full transition-all text-sm"
            >
              Book Free Audit
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="pt-32 sm:pt-40 pb-20 sm:pb-32 relative overflow-hidden">
        {/* Gradient Orbs */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-10 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>

        <div className={`max-w-5xl mx-auto px-4 sm:px-6 text-center relative z-10 transition-all duration-1000 ${
          visibleSections.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-8">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-300 font-medium">UK SME AUTOMATION CONSULTANT</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            Cut Through the
            <span className="block bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              Automation Noise
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            The automation skeptic's consultant. Honest, research-led guidance for businesses 
            drowning in tool choices. We tell you what to automate—and what NOT to.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="mailto:owlworklabs@gmail.com?subject=Auto-Mate Audit Request"
              className="flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full transition-all text-lg shadow-lg shadow-emerald-500/25 hover:shadow-emerald-400/40"
            >
              <Mail className="w-5 h-5" />
              Book Free Audit
            </a>
            <a 
              href="#process"
              className="flex items-center gap-2 px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-full transition-all border border-slate-700"
            >
              See How It Works
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-16 sm:py-20 bg-slate-900/50 border-y border-slate-800">
        <div className={`max-w-6xl mx-auto px-4 sm:px-6 transition-all duration-1000 ${
          visibleSections.stats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-center text-slate-400 mb-10 text-sm uppercase tracking-wider">Research Credibility You Can Trust</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { value: 200, suffix: '+', label: 'Tools Analyzed' },
              { value: 1000, suffix: '+', label: 'Research Hours' },
              { value: 50, suffix: '+', label: 'Frameworks Tested' },
              { value: 100, suffix: '%', label: 'UK Business Focus' }
            ].map((stat) => (
              <div key={stat.label} className="text-center p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50">
                <div className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-slate-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section id="whofor" className="py-20 sm:py-28">
        <div className={`max-w-6xl mx-auto px-4 sm:px-6 transition-all duration-1000 ${
          visibleSections.whofor ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-16">
            Who This Is <span className="text-emerald-400">For</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* This is for you */}
            <div className="p-8 rounded-3xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30">
              <h3 className="text-xl font-bold text-emerald-400 mb-6 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6" />
                This is for you if
              </h3>
              <ul className="space-y-4">
                {[
                  "You're a UK SME with 50-500 employees",
                  "You're overwhelmed by automation tool choices",
                  "You need unbiased, research-led guidance",
                  "You want to reduce risk, not just costs",
                  "You value transparency and honest advice"
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* This is NOT for you */}
            <div className="p-8 rounded-3xl bg-slate-800/50 border border-slate-700/50">
              <h3 className="text-xl font-bold text-slate-400 mb-6 flex items-center gap-2">
                <XCircle className="w-6 h-6" />
                This is not for you if
              </h3>
              <ul className="space-y-4">
                {[
                  "You're looking for quick fixes or magic solutions",
                  "You want someone to tell you what you want to hear",
                  "You're not ready to invest time in proper planning",
                  "You expect instant results without process",
                  "You're outside the UK or have fewer than 50 employees"
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-400">
                    <XCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 sm:py-28 bg-slate-900/50">
        <div className={`max-w-6xl mx-auto px-4 sm:px-6 transition-all duration-1000 ${
          visibleSections.process ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="text-center mb-16">
            <p className="text-emerald-400 font-semibold mb-4 text-sm uppercase tracking-wider">Our Process</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Transparent. Methodical. <span className="text-emerald-400">Effective.</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              No black boxes. No hidden steps. Just clear, methodical execution.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, i) => (
              <div 
                key={step.num}
                className="group p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50 hover:border-emerald-500/50 transition-all duration-300 hover:-translate-y-2"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-bold text-emerald-400/50">{step.num}</span>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                    <step.icon className="w-5 h-5 text-emerald-400" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section id="principles" className="py-20 sm:py-28">
        <div className={`max-w-6xl mx-auto px-4 sm:px-6 transition-all duration-1000 ${
          visibleSections.principles ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="text-center mb-16">
            <p className="text-emerald-400 font-semibold mb-4 text-sm uppercase tracking-wider">Our Principles</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">
              Why <span className="text-emerald-400">Trust</span> Us?
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {principles.map((principle) => (
              <div 
                key={principle.title}
                className="p-8 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 hover:border-emerald-500/30 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-5">
                  <principle.icon className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{principle.title}</h3>
                <p className="text-slate-400 leading-relaxed">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 sm:py-28 bg-slate-900/50">
        <div className={`max-w-3xl mx-auto px-4 sm:px-6 transition-all duration-1000 ${
          visibleSections.faq ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="text-center mb-12">
            <p className="text-emerald-400 font-semibold mb-4 text-sm uppercase tracking-wider">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-bold">
              Frequently Asked <span className="text-emerald-400">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <FAQItem
                key={faq.question}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === i}
                onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-20 sm:py-28 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-cyan-500/10"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl"></div>

        <div className={`max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10 transition-all duration-1000 ${
          visibleSections.cta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            Ready to Cut Through the <span className="text-emerald-400">Noise</span>?
          </h2>
          <p className="text-lg sm:text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Begin with a free audit. No pressure, no commitment. Just honest, 
            research-led guidance for your automation journey.
          </p>
          
          <a 
            href="mailto:owlworklabs@gmail.com?subject=Auto-Mate Audit Request"
            className="inline-flex items-center gap-3 px-10 py-5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full transition-all text-lg shadow-lg shadow-emerald-500/25 hover:shadow-emerald-400/40 hover:scale-105"
          >
            <Mail className="w-6 h-6" />
            Book Your Free Audit
          </a>

          <p className="mt-8 text-slate-500 text-sm">
            All engagements protected under NDA • GDPR Compliant • UK Registered
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="OwlWorkLabs" className="w-8 h-8" />
              <span className="text-slate-400">Auto-Mate by OwlWorkLabs</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
              <a href="mailto:owlworklabs@gmail.com" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            © {new Date().getFullYear()} OwlWorkLabs Ltd. All rights reserved. · Company No. 16713122
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AutoMatePage;
